import { Component, OnInit, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { ToastService } from '../../services/toast.service';
import { Toast, ToastPosition, ToastOffset } from '../../models/toast.model';

@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './toast.component.html',
  styleUrls: ['./toast.component.scss'],
  animations: [
    trigger('fadeInSlide', [
      state('void', style({
        opacity: 0,
        transform: 'translateX(100%)'
      })),
      state('visible', style({
        opacity: 1,
        transform: 'translateX(0)'
      })),
      state('hidden', style({
        opacity: 0,
        transform: 'translateX(100%)'
      })),
      transition('void => visible', animate('300ms ease-out')),
      transition('visible => hidden', animate('300ms ease-in'))
    ]),
    trigger('slide', [
      state('void', style({
        transform: 'translateX(100%)'
      })),
      state('visible', style({
        transform: 'translateX(0)'
      })),
      state('hidden', style({
        transform: 'translateX(100%)'
      })),
      transition('void => visible', animate('300ms ease-out')),
      transition('visible => hidden', animate('300ms ease-in'))
    ])
  ]
})
export class ToastComponent implements OnInit {
  toasts: Toast[] = [];
  toastsByPosition: Map<ToastPosition, Toast[]> = new Map();
  offset: ToastOffset = {};

  private destroyRef = inject(DestroyRef);

  constructor(private toastService: ToastService) {}

  ngOnInit(): void {
    this.toastService.getToasts()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(toasts => {
        this.toasts = toasts;
        this.groupToastsByPosition();
      });

    this.toastService.getOffset()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(offset => {
        this.offset = offset;
      });
  }

  private groupToastsByPosition(): void {
    this.toastsByPosition.clear();
    
    this.toasts.forEach(toast => {
      const position = toast.position || 'top-right';
      if (!this.toastsByPosition.has(position)) {
        this.toastsByPosition.set(position, []);
      }
      this.toastsByPosition.get(position)!.push(toast);
    });
  }

  getPositionClass(position: ToastPosition): string {
    const baseClasses: { [key in ToastPosition]: string } = {
      'top-right': 'right-4',
      'top-left': 'left-4',
      'bottom-right': 'bottom-4 right-4',
      'bottom-left': 'bottom-4 left-4',
      'top-center': 'left-1/2 -translate-x-1/2',
      'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2'
    };
    return baseClasses[position];
  }

  getPositionStyle(position: ToastPosition): { [key: string]: string } {
    const style: { [key: string]: string } = {};
    
    // Aplicar offset para posiciones top
    if (position.startsWith('top')) {
      const topOffset = this.offset.top ?? 16; // 1rem = 16px por defecto
      style['top'] = `${topOffset}px`;
    }

    // Aplicar offset para posiciones bottom
    if (position.startsWith('bottom')) {
      const bottomOffset = this.offset.bottom ?? 16;
      style['bottom'] = `${bottomOffset}px`;
    }

    // Aplicar offset lateral si existe
    if (this.offset.left !== undefined && (position.includes('left') || position.includes('center'))) {
      style['left'] = `${this.offset.left}px`;
    }

    if (this.offset.right !== undefined && position.includes('right')) {
      style['right'] = `${this.offset.right}px`;
    }

    return style;
  }

  getIconConfig(type: string): { icon: string; colorClass: string } {
    const configs = {
      success: {
        icon: 'check',
        colorClass: 'bg-green-500'
      },
      error: {
        icon: 'exclamation',
        colorClass: 'bg-red-500'
      },
      warning: {
        icon: 'warning',
        colorClass: 'bg-orange-500'
      },
      info: {
        icon: 'info',
        colorClass: 'bg-blue-500'
      }
    };
    return configs[type as keyof typeof configs] || configs.info;
  }

  getActionButtonClass(type: string, isPrimary: boolean): string {
    if (!isPrimary) {
      return 'text-gray-500 hover:text-gray-700 text-sm font-medium px-4 py-2.5 transition-colors';
    }

    const classes = {
      success: 'bg-green-500 hover:bg-green-600 text-white font-semibold text-sm px-5 py-2.5 rounded-lg transition-colors',
      error: 'bg-red-500 hover:bg-red-600 text-white font-semibold text-sm px-5 py-2.5 rounded-lg transition-colors',
      warning: 'border-2 border-orange-500 text-orange-500 hover:bg-orange-50 font-semibold text-sm px-5 py-2 rounded-lg transition-colors',
      info: 'bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold text-sm px-5 py-2.5 rounded-lg transition-colors'
    };
    
    return classes[type as keyof typeof classes] || classes.info;
  }

  getAnimationState(toast: Toast): string {
    return toast.visible ? 'visible' : 'hidden';
  }

  dismiss(id: string): void {
    this.toastService.dismiss(id);
  }

  handleAction(toast: Toast, handler: () => void): void {
    handler();
    this.dismiss(toast.id);
  }
}
